import type { CmmdContextPaths } from "@cmmd-forge/contracts";

export type CloudEnvironmentLifecycleFacts =
  CmmdContextPaths["/api/forge/context/cloud-projects"]["get"]["responses"][200]["content"]["application/json"]["data"]["environments"][number]["lifecycle"];

export type CloudEnvironmentLifecycleState =
  | "provisioning"
  | "sleeping"
  | "starting"
  | "ready"
  | "unavailable"
  | "degraded"
  | "needs-attention"
  | "paused"
  | "deleting"
  | "unknown";

export interface CloudEnvironmentLifecycleProjection {
  readonly state: CloudEnvironmentLifecycleState;
  readonly label: string;
  readonly detail: string | null;
  readonly tone: "success" | "warning" | "destructive" | "muted";
  readonly automaticWakeAllowed: boolean;
  /** Local to this Shell. It is observation only and never initiates a wake. */
  readonly shellConnected: boolean;
  /** True only when current control-plane facts are healthy but lack execution proof. */
  readonly shellMayEstablishReady: boolean;
}

const UNKNOWN_ENVIRONMENT_LIFECYCLE: CloudEnvironmentLifecycleProjection = {
  state: "unknown",
  label: "Unknown",
  detail: "Current Environment availability has not been observed.",
  tone: "muted",
  automaticWakeAllowed: false,
  shellConnected: false,
  shellMayEstablishReady: false,
};

function projected(
  state: CloudEnvironmentLifecycleState,
  label: string,
  detail: string | null,
  tone: CloudEnvironmentLifecycleProjection["tone"],
  automaticWakeAllowed: boolean,
  shellConnected: boolean,
  shellMayEstablishReady = false,
): CloudEnvironmentLifecycleProjection {
  return {
    state,
    label,
    detail,
    tone,
    automaticWakeAllowed,
    shellConnected,
    shellMayEstablishReady,
  };
}

function projectOwnerIntent(
  lifecycle: CloudEnvironmentLifecycleFacts,
  shellConnected: boolean,
): CloudEnvironmentLifecycleProjection | null {
  if (lifecycle.ownerIntent === "paused") {
    return projected(
      "paused",
      "Paused",
      "Resume before live work can wake this Environment.",
      "warning",
      false,
      shellConnected,
    );
  }
  if (lifecycle.ownerIntent === "deleting") {
    return projected(
      "deleting",
      "Deleting",
      "This Environment is being removed.",
      "muted",
      false,
      shellConnected,
    );
  }
  return null;
}

function projectExecutionAdmission(
  lifecycle: CloudEnvironmentLifecycleFacts,
  shellConnected: boolean,
): CloudEnvironmentLifecycleProjection | null {
  if (lifecycle.executionAdmission.state === "allowed") return null;
  return projected(
    "unavailable",
    "Unavailable",
    "This Environment is not accepting live work while it drains.",
    "warning",
    false,
    shellConnected,
  );
}

/**
 * Converts forge-context lifecycle facts into the one Environment state the
 * Settings row may show. Missing or stale facts stay Unknown. This function
 * never reads a guest, so callers may use it during passive reads.
 */
export function projectCloudEnvironmentLifecycle(
  lifecycle: CloudEnvironmentLifecycleFacts | null | undefined,
  shellConnected = false,
): CloudEnvironmentLifecycleProjection {
  if (lifecycle === null || lifecycle === undefined) return UNKNOWN_ENVIRONMENT_LIFECYCLE;
  const ownerProjection = projectOwnerIntent(lifecycle, shellConnected);
  if (ownerProjection !== null) return ownerProjection;
  if (lifecycle.ownerIntent !== "active") return UNKNOWN_ENVIRONMENT_LIFECYCLE;
  const admissionProjection = projectExecutionAdmission(lifecycle, shellConnected);
  if (admissionProjection !== null) return admissionProjection;

  const { provider, guestReadiness, activeWork } = lifecycle;
  if (
    provider.observedAt === null ||
    provider.failure !== null ||
    provider.state === "absent" ||
    provider.state === "unknown"
  ) {
    return projected(
      "unknown",
      "Unknown",
      "Current provider availability has not been observed.",
      "muted",
      lifecycle.automaticWakeAllowed,
      shellConnected,
    );
  }
  if (provider.state === "sleeping") {
    return projected(
      "sleeping",
      "Sleeping",
      "Live work wakes this Environment automatically.",
      "muted",
      lifecycle.automaticWakeAllowed,
      shellConnected,
    );
  }
  if (
    guestReadiness.observedAt === null ||
    guestReadiness.failure !== null ||
    guestReadiness.state === "unknown"
  ) {
    return projected(
      "unknown",
      "Unknown",
      "Current guest readiness has not been observed.",
      "muted",
      lifecycle.automaticWakeAllowed,
      shellConnected,
    );
  }
  if (guestReadiness.state === "starting") {
    return projected(
      "starting",
      "Starting",
      "Preparing live execution.",
      "warning",
      lifecycle.automaticWakeAllowed,
      shellConnected,
    );
  }
  if (guestReadiness.state === "degraded") {
    return projected(
      "degraded",
      "Degraded",
      "A required service needs attention.",
      "warning",
      lifecycle.automaticWakeAllowed,
      shellConnected,
    );
  }
  if (guestReadiness.state === "failed") {
    return projected(
      "needs-attention",
      "Needs attention",
      "The readiness check failed.",
      "destructive",
      lifecycle.automaticWakeAllowed,
      shellConnected,
    );
  }
  if (shellConnected) {
    return projected("ready", "Ready", null, "success", lifecycle.automaticWakeAllowed, true);
  }
  if (activeWork.state === "active") {
    return projected(
      "ready",
      "Ready",
      null,
      "success",
      lifecycle.automaticWakeAllowed,
      shellConnected,
    );
  }
  return projected(
    "unknown",
    "Unknown",
    "No current execution proof is available.",
    "muted",
    lifecycle.automaticWakeAllowed,
    shellConnected,
    true,
  );
}

export function withShellConnectionProof(
  lifecycle: CloudEnvironmentLifecycleProjection,
  shellConnected: boolean,
): CloudEnvironmentLifecycleProjection {
  if (!shellConnected || !lifecycle.shellMayEstablishReady) {
    return lifecycle;
  }
  return projected("ready", "Ready", null, "success", lifecycle.automaticWakeAllowed, true);
}
